import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import { prepareImageForStorageUpload } from './prepareImageForStorageUpload';
import type { DemBlockedFilter } from './demCustomers';
import type { DemItem, DemItemSort } from '../types/dem';

const ITEM_SELECT =
  'code,description,uom,packing,image_url,is_blocked,inventory,stock_status,chinese_description,status';

function rowToItem(row: Record<string, unknown>): DemItem {
  const inventoryRaw = row.inventory;
  let inventory = 0;
  if (typeof inventoryRaw === 'number') inventory = inventoryRaw;
  else if (inventoryRaw != null) {
    const parsed = Number(inventoryRaw);
    inventory = Number.isFinite(parsed) ? parsed : 0;
  }

  return {
    code: String(row.code ?? ''),
    description: String(row.description ?? ''),
    uom: String(row.uom ?? ''),
    packing: String(row.packing ?? ''),
    image_url: String(row.image_url ?? ''),
    is_blocked: Boolean(row.is_blocked),
    inventory,
    stock_status: String(row.stock_status ?? ''),
    chinese_description: String(row.chinese_description ?? ''),
    status: String(row.status ?? ''),
  };
}

export type FetchItemsResult = {
  rows: DemItem[];
  total: number;
};

export async function fetchDemItems(options: {
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: DemItemSort;
  blockedFilter?: DemBlockedFilter;
}): Promise<FetchItemsResult> {
  const blockedFilter = options.blockedFilter ?? 'all';
  if (blockedFilter === 'none') {
    return { rows: [], total: 0 };
  }

  const page = Math.max(1, options.page ?? 1);
  const pageSize = Math.min(200, Math.max(25, options.pageSize ?? 50));
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  const sort = options.sort ?? 'code_asc';

  const db = getDbSupabase('dem');
  let query = db
    .from('items')
    .select(ITEM_SELECT, { count: 'exact' })
    .eq('status', 'Active')
    .not('code', 'ilike', 'AP-%');

  if (blockedFilter === 'unblocked') {
    query = query.or('is_blocked.eq.false,is_blocked.is.null');
  } else if (blockedFilter === 'blocked') {
    query = query.eq('is_blocked', true);
  }

  const search = options.search?.trim() ?? '';
  if (search) {
    const escaped = search.replace(/[%_]/g, '\\$&');
    query = query.or(`code.ilike.%${escaped}%,description.ilike.%${escaped}%`);
  }

  const sortColumn = sort.startsWith('description') ? 'description' : 'code';
  const ascending = sort.endsWith('_asc');

  let orderedQuery = query.order(sortColumn, { ascending, nullsFirst: false });
  if (sortColumn !== 'code') {
    orderedQuery = orderedQuery.order('code', { ascending: true });
  }

  const { data, error, count } = await orderedQuery.range(from, to);
  if (error) throw new Error(formatSupabaseError(error));

  return {
    rows: (data ?? []).map((row) => rowToItem(row as Record<string, unknown>)),
    total: count ?? 0,
  };
}

export async function fetchDemItem(code: string): Promise<DemItem | null> {
  const db = getDbSupabase('dem');
  const { data, error } = await db.from('items').select(ITEM_SELECT).eq('code', code).maybeSingle();
  if (error) throw new Error(formatSupabaseError(error));
  if (!data) return null;
  return rowToItem(data as Record<string, unknown>);
}

function safeItemStoragePath(code: string): string {
  const safe = code.replace(/[^a-zA-Z0-9_-]/g, '');
  return `items/${safe || 'item'}.jpg`;
}

export async function uploadDemItemPhoto(code: string, file: File): Promise<string> {
  const db = getDbSupabase('dem');
  const prepared = await prepareImageForStorageUpload(file);
  // Item photos are stored as .jpg path historically.
  const path = safeItemStoragePath(code);

  const { error: uploadError } = await db.storage.from('product-images').upload(path, prepared.body, {
    upsert: true,
    contentType: prepared.contentType,
  });

  if (uploadError) throw new Error(formatSupabaseError(uploadError));

  const { data: pub } = db.storage.from('product-images').getPublicUrl(path);
  const imageUrl = `${pub.publicUrl}?t=${Date.now()}`;

  const { data, error } = await db
    .from('items')
    .update({ image_url: imageUrl })
    .eq('code', code)
    .select(ITEM_SELECT)
    .maybeSingle();

  if (error) throw new Error(formatSupabaseError(error));
  if (!data) {
    throw new Error('Photo uploaded but item row was not updated. Check write permissions.');
  }

  return imageUrl;
}

export async function removeDemItemPhoto(code: string): Promise<DemItem> {
  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('items')
    .update({ image_url: null })
    .eq('code', code)
    .select(ITEM_SELECT)
    .maybeSingle();

  if (error) throw new Error(formatSupabaseError(error));
  if (!data) {
    throw new Error('Remove returned no rows — check write permissions on items.');
  }

  return rowToItem(data as Record<string, unknown>);
}

export function itemHasPhoto(item: Pick<DemItem, 'image_url'>): boolean {
  return Boolean(item.image_url?.trim());
}
